import { useMemo } from "react";
import { LineChart, Shield, User, TrendingUp } from "lucide-react";
import { BlockLink, SchoolLink } from "@/components/DrilldownLink";
import {
  buildBlockRankCards,
  buildGeoHero,
  buildGeoOperationalActions,
  buildGpiHbarChart,
  buildSizeChartBars,
  buildTopSchoolRows,
  fmt,
} from "@/utils/ageEnrolmentDerived";
import "./AgeEnrolmentGeographyTab.css";

const scrollToId = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

const ACTION_ICONS = {
  orange: TrendingUp,
  green: Shield,
  "": User,
};

const PIN_POSITIONS = ["p1", "p2", "p3", "p4"];

const AgeEnrolmentGeographyTab = ({
  overview = {},
  blockData = [],
  schoolSizeData = null,
  topSchools = [],
}) => {
  const hero = useMemo(
    () => buildGeoHero(overview, blockData, schoolSizeData),
    [overview, blockData, schoolSizeData]
  );
  const rankCards = useMemo(() => buildBlockRankCards(blockData), [blockData]);
  const gpiBars = useMemo(() => buildGpiHbarChart(blockData), [blockData]);
  const sizeBars = useMemo(() => buildSizeChartBars(schoolSizeData), [schoolSizeData]);
  const actions = useMemo(() => buildGeoOperationalActions(blockData), [blockData]);
  const schoolRows = useMemo(() => buildTopSchoolRows(topSchools), [topSchools]);

  const sizePill =
    schoolSizeData != null
      ? `Median ${Math.round(schoolSizeData.median_size)} · Avg ${Math.round(schoolSizeData.avg_size)}`
      : "Size bands";

  return (
    <div className="ageo-tab" data-testid="age-enrolment-geography">
      <section className="ageo-hero-card" id="ageo-hero">
        <div className="ageo-geo-hero">
          <div className="ageo-map-visual" aria-hidden="true">
            <div className="ageo-map-blob b1" />
            <div className="ageo-map-blob b2" />
            <div className="ageo-map-blob b3" />
            {hero.mapPins.map((pin, idx) => (
              <span key={PIN_POSITIONS[idx]} className={`ageo-pin ${PIN_POSITIONS[idx]}`}>
                {pin.value}
              </span>
            ))}
            <div className="ageo-map-caption">
              <b>{hero.captionTitle}</b>
              <span>{hero.captionBody}</span>
            </div>
          </div>

          <div className="ageo-hero-copy">
            <h2>School Geography Diagnostic</h2>
            <p>
              The current tab had long tables first, pushing insights below the fold. This redesign
              starts with geographic concentration, then exposes block parity, school-size load, and
              high-enrolment school risk.
            </p>
            <div className="ageo-status-row">
              {hero.chips.map((chip) => (
                <span key={chip.label} className={`ageo-status-chip ${chip.className}`.trim()}>
                  {chip.label}
                </span>
              ))}
            </div>
            <div className="ageo-metric-grid">
              {hero.metrics.map((metric) => (
                <article
                  key={metric.label}
                  className={`ageo-metric-card ${metric.warn ? "warn" : ""}`.trim()}
                >
                  <small>{metric.label}</small>
                  <strong>{metric.value}</strong>
                  <span>{metric.sub}</span>
                </article>
              ))}
            </div>
          </div>
        </div>
      </section>

      <div className="ageo-section-title" id="ageo-blocks">
        <div>
          <h2>Block Concentration and Parity</h2>
          <p>
            Tables are converted into ranked cards and compact charts so users can identify where
            enrolment load and gender parity intervention are needed.
          </p>
        </div>
        <button type="button" className="ageo-section-action" onClick={() => scrollToId("ageo-schools")}>
          View school list →
        </button>
      </div>

      <section className="ageo-two-col">
        <article className="ageo-panel">
          <div className="ageo-panel-head">
            <div>
              <h3>Top Block Enrolment Ranking</h3>
              <p>Primary scan of block-level load, average school size and GPI quality.</p>
            </div>
            <span className="ageo-pill">Top 8 blocks</span>
          </div>
          <div className="ageo-rank-grid">
            {rankCards.map((card) => (
              <div key={card.name} className="ageo-rank-card">
                <span className="ageo-rank-no">{card.rank}</span>
                <div className="ageo-rank-name">
                  <b>
                    {card.blockCode ? (
                      <BlockLink blockCode={card.blockCode}>{card.name}</BlockLink>
                    ) : (
                      card.name
                    )}
                  </b>
                  <span>
                    {card.schools} schools · Avg {card.avg} per school
                  </span>
                </div>
                <span className={`ageo-score-pill ${card.gpiClass}`.trim()}>GPI {card.gpi}</span>
                <span className="ageo-avg">{card.status}</span>
              </div>
            ))}
          </div>
        </article>

        <article className="ageo-panel">
          <div className="ageo-panel-head">
            <div>
              <h3>Block-Wise Gender Parity Index</h3>
              <p>GPI below 0.90 is highlighted as a review zone, while above-parity outliers are separated.</p>
            </div>
            <span className="ageo-pill">GPI</span>
          </div>
          <div className="ageo-hbar-list">
            {gpiBars.map((bar) => (
              <div key={bar.name} className="ageo-hbar-row">
                <div className="ageo-hbar-label">{bar.name}</div>
                <div className="ageo-hbar-track">
                  <div
                    className={`ageo-hbar ${bar.barClass}`.trim()}
                    style={{ width: `${bar.widthPct}%` }}
                  />
                </div>
                <div className="ageo-hbar-value">{bar.gpi}</div>
              </div>
            ))}
          </div>
        </article>
      </section>

      <section className="ageo-two-col">
        <article className="ageo-panel">
          <div className="ageo-panel-head">
            <div>
              <h3>School Size Distribution</h3>
              <p>Most schools are small, but medium and large schools need capacity monitoring.</p>
            </div>
            <span className="ageo-pill">{sizePill}</span>
          </div>
          <div className="ageo-size-chart">
            {sizeBars.map((bar) => (
              <div key={bar.band} className="ageo-size-bar">
                <i style={{ height: `${bar.heightPx}px` }} />
                {bar.band}
              </div>
            ))}
          </div>
        </article>

        <article className="ageo-panel">
          <div className="ageo-panel-head">
            <div>
              <h3>Operational Interpretation</h3>
              <p>Use this view to convert geography into district-level monitoring actions.</p>
            </div>
            <span className="ageo-pill">Action lens</span>
          </div>
          <div className="ageo-action-grid">
            {actions.map((action) => {
              const Icon = ACTION_ICONS[action.iconClass] || LineChart;
              return (
                <div key={action.title} className="ageo-action-item">
                  <div className={`ageo-icon-box ${action.iconClass}`.trim()}>
                    <Icon size={22} strokeWidth={2} />
                  </div>
                  <div>
                    <h4>{action.title}</h4>
                    <p>{action.body}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </article>
      </section>

      <div className="ageo-section-title" id="ageo-schools">
        <div>
          <h2>Top Schools by Enrolment</h2>
          <p>
            High-enrolment schools are shown as a focused operational list instead of a visually flat
            table.
          </p>
        </div>
        <button type="button" className="ageo-section-action" onClick={() => scrollToId("ageo-hero")}>
          Back to top ↑
        </button>
      </div>

      <section className="ageo-panel ageo-table-card">
        <div className="ageo-table-head">
          <div>
            <h3>Top 10 Schools Requiring Capacity and Parity Review</h3>
            <p>Sorted by enrolment with block, GPI and recommended monitoring category.</p>
          </div>
          <span className="ageo-pill">Top 10</span>
        </div>
        <table aria-label="Top schools by enrolment">
          <thead>
            <tr>
              <th>#</th>
              <th>School Name</th>
              <th>Block</th>
              <th>Boys</th>
              <th>Girls</th>
              <th>Total</th>
              <th>GPI</th>
              <th>Review Flag</th>
            </tr>
          </thead>
          <tbody>
            {schoolRows.map((row) => (
              <tr key={row.udiseCode || row.rank}>
                <td>{row.rank}</td>
                <td className="ageo-school-name">
                  {row.udiseCode ? (
                    <SchoolLink udiseCode={row.udiseCode}>{row.schoolName}</SchoolLink>
                  ) : (
                    row.schoolName
                  )}
                </td>
                <td>{row.blockName}</td>
                <td>{row.showDash ? "—" : fmt(row.boys)}</td>
                <td>{row.showDash ? "—" : fmt(row.girls)}</td>
                <td>{row.total > 0 ? fmt(row.total) : "High"}</td>
                <td>
                  <span className={`ageo-tag ${row.gpiClass}`.trim()}>{row.gpi}</span>
                </td>
                <td>{row.reviewFlag}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    </div>
  );
};

export default AgeEnrolmentGeographyTab;
